import {
  patchState,
  signalStoreFeature,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import {
  PaginatedResult,
  PaginationFilter,
} from '../../shared/models/pagination.model';
import { computed, inject, ProviderToken } from '@angular/core';
import {
  Entity,
  setError,
  setLoaded,
  setLoading,
  withCallState,
} from '@angular-architects/ngrx-toolkit';
import { CrudService } from '../../shared/models/crud-service.model';
import { Router } from '@angular/router';
import { SnackbarService } from '../../core/services/snackbar.service';
import { Subscription } from 'rxjs';

export interface EntitiesManagementState<E extends Entity> {
  entities: E[];
  selectedEntity: E | null;
  currentPage: number;
  pageSize: number;
  totalItems: number;
  totalItemsFromDB: number;
  totalPages: number;
  searchQuery: string;
  sortBy: string;
  showUnverifiedOnly: boolean;
}

const initialEntitiesManagementState: EntitiesManagementState<undefined> = {
  entities: [],
  selectedEntity: null,
  currentPage: 1,
  pageSize: 20,
  totalItems: 0,
  totalItemsFromDB: 0,
  totalPages: 0,
  searchQuery: '',
  sortBy: 'name',
  showUnverifiedOnly: false,
};

export function withEntitiesManagement<E extends Entity>(
  serviceType: ProviderToken<CrudService<E>>,
  collectionName: string,
  routeName: string
) {
  return signalStoreFeature(
    withState<EntitiesManagementState<E>>(initialEntitiesManagementState),
    withCallState({ collection: collectionName }),
    withComputed(({ currentPage, totalPages, totalItems, pageSize }) => ({
      hasNextPage: computed(() => currentPage() < totalPages()),
      hasPreviousPage: computed(() => currentPage() > 1),
      startItem: computed(() => {
        if (totalItems() === 0) return 0;
        return (currentPage() - 1) * pageSize() + 1;
      }),
      endItem: computed(() => {
        const end = currentPage() * pageSize();
        return Math.min(end, totalItems());
      }),
    })),
    withMethods(
      (
        store,
        service = inject(serviceType),
        router = inject(Router),
        snackbarService = inject(SnackbarService)
      ) => {
        let loadEntitiesSub: Subscription | null = null;

        return {
          loadEntities(append = false): void {
            patchState(store, setLoading(collectionName));

            const paginationFilter: PaginationFilter = {
              page: store.currentPage(),
              limit: store.pageSize(),
              search: store.searchQuery(),
              isVerified: store.showUnverifiedOnly() ? false : undefined,
            };

            if (loadEntitiesSub) {
              loadEntitiesSub.unsubscribe();
            }

            loadEntitiesSub = service
              .loadWithFilter(paginationFilter)
              .subscribe({
                next: (result) => {
                  patchState(
                    store,
                    {
                      entities: append
                        ? [...store.entities(), ...result.data]
                        : result.data,
                    },
                    updatePaginationInfo(result),
                    setLoaded(collectionName)
                  );
                },
                error: (error) => {
                  const errorMessage =
                    error.error?.message || 'Failed to load entities';
                  snackbarService.show(errorMessage, 'error');
                  patchState(store, {
                    ...setError(errorMessage, collectionName),
                    ...setLoaded(collectionName),
                  });
                },
              });

            this.loadTotalCountFromDB();
          },

          // Navigation methods
          nextPage(append = false): void {
            if (store.currentPage() < store.totalPages()) {
              this.loadPage(store.currentPage() + 1, append);
            }
          },

          // Search and filter methods
          updateFiltersAndLoad(filters: {
            page?: number;
            size?: number;
            search?: string;
            unverified?: boolean;
          }): void {
            patchState(store, {
              currentPage: filters.page,
              pageSize: filters.size,
              searchQuery: filters.search,
              showUnverifiedOnly: filters.unverified,
            });
            this.loadEntities();
          },

          search(searchQuery: string): void {
            patchState(store, { searchQuery });
            this.loadPage(1); // Reset to first page when searching
          },

          refresh(): void {
            this.loadPage(store.currentPage());
          },

          loadPage(page: number, append = false): void {
            patchState(store, { currentPage: page });
            this.loadEntities(append);
          },

          loadEntityById(id: string): void {
            patchState(store, setLoading(collectionName));

            service.loadById(id).subscribe({
              next: (entity) => {
                patchState(
                  store,
                  {
                    selectedEntity: entity,
                  },
                  setLoaded(collectionName)
                );
              },
              error: (error) => {
                const errorMessage =
                  error.error?.message || 'Failed to load entity';
                snackbarService.show(errorMessage, 'error');
                console.error('Failed to load entity', error);
                patchState(store, setError(errorMessage, collectionName));
              },
            });
          },

          createEntity(
            entity: Partial<Omit<E, 'id' | 'createdAt' | 'updatedAt'>>
          ) {
            patchState(store, setLoading(collectionName));

            service.create(entity).subscribe({
              next: (createdEntity) => {
                snackbarService.show('Entity created successfully', 'success');
                this.loadTotalCountFromDB();
                router.navigate(['/', routeName, createdEntity.id], {
                  replaceUrl: true,
                });

                patchState(
                  store,
                  {
                    selectedEntity: createdEntity,
                  },
                  setLoaded(collectionName)
                );
              },
              error: (error) => {
                const errorMessage =
                  error.error?.message || 'Failed to create entity';
                snackbarService.show(errorMessage, 'error');
                patchState(
                  store,
                  setError(errorMessage, collectionName),
                  setLoaded(collectionName)
                );
              },
            });
          },
          updateEntity(id: string, entity: Partial<E>) {
            patchState(store, setLoading(collectionName));

            service.update(id, entity).subscribe({
              next: (updatedEntity) => {
                snackbarService.show('Entity updated successfully', 'success');
                patchState(
                  store,
                  {
                    selectedEntity: {
                      ...store.selectedEntity(),
                      ...updatedEntity,
                    },
                  },
                  setLoaded(collectionName)
                );
              },
              error: (error) => {
                const errorMessage =
                  error.error?.message || 'Failed to update entity';
                snackbarService.show(errorMessage, 'error');
                patchState(
                  store,
                  setError(errorMessage, collectionName),
                  setLoaded(collectionName)
                );
              },
            });
          },

          deleteEntity(id: string) {
            patchState(store, setLoading(collectionName));

            service.delete(id).subscribe({
              next: () => {
                snackbarService.show('Entity deleted successfully', 'success');
                patchState(
                  store,
                  {
                    selectedEntity: null,
                  },
                  setLoaded(collectionName)
                );

                this.refresh();
              },
              error: (error) => {
                const errorMessage =
                  error.error?.message || 'Failed to delete entity';
                snackbarService.show(errorMessage, 'error');
                patchState(store, setError(errorMessage, collectionName));
              },
            });
          },

          deleteMany(ids: string[]) {
            patchState(store, setLoading(collectionName));

            service.deleteMany(ids).subscribe({
              next: () => {
                snackbarService.show(
                  'Entities deleted successfully',
                  'success'
                );
                patchState(store, setLoaded(collectionName));
                this.refresh();
              },
              error: (error) => {
                const errorMessage =
                  error.error?.message || 'Failed to delete entities';
                snackbarService.show(errorMessage, 'error');
                patchState(store, setError(errorMessage, collectionName));
                this.refresh();
              },
            });
          },

          clearSelectedEntity() {
            patchState(store, { selectedEntity: null });
          },

          loadTotalCountFromDB(): void {
            patchState(store, setLoading(collectionName));

            service.getTotalCount().subscribe({
              next: (result) => {
                patchState(
                  store,
                  {
                    totalItemsFromDB: result.total,
                  },
                  setLoaded(collectionName)
                );
              },
              error: (error) => {
                const errorMessage =
                  error.error?.message || 'Failed to load total count';
                snackbarService.show(errorMessage, 'error');
                patchState(store, setError(errorMessage, collectionName));
              },
            });
          },

          restore() {
            patchState(store, {
              entities: [],
              selectedEntity: null,
            });
          },
        };
      }
    )
  );
}

export function updatePaginationInfo(
  result: PaginatedResult<unknown>
): Partial<{
  totalItems: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}> {
  return {
    totalItems: result.totalItems,
    totalPages: result.totalPages,
    currentPage: result.currentPage,
    pageSize: result.pageSize,
  };
}
